import React, { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, CircleNotch, X, Funnel, CaretDown, Check, ArrowSquareOut, Stack } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, truncMid } from "@/lib/format";
import { assetLabel, decodeAssetNameSmart, TokenLogo, Cip68Badge, VerifiedTick, WindowTabs } from "@/components/assets/parts";
import { useTokenMeta, useRequestMeta } from "@/components/assets/tokenMeta";

const HEX = /^[0-9a-f]+$/;
const FILTERS = [
  { key: "all", label: "All filters" },
  { key: "name", label: "Name" },
  { key: "policy", label: "Policy ID" },
  { key: "fingerprint", label: "Fingerprint" },
];

// Home-style search for the Native Assets dashboard. No server-side name search
// exists upstream, so suggestions come from a local index built from the
// trending + most-active assets (30d); a full asset id / policy id always works
// directly. The right-hand toggle is the shared 24h/7d/30d window.
export const AssetSearchBar = ({ onPickAsset, onOpenPolicy, win, setWin }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [index, setIndex] = useState(null);
  const [loadingIdx, setLoadingIdx] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const indexRef = useRef(null);
  const loadingRef = useRef(false);
  indexRef.current = index;

  const loadIndex = async () => {
    if (indexRef.current || loadingRef.current) return;
    loadingRef.current = true;
    setLoadingIdx(true);
    try {
      const [t, a] = await Promise.all([
        http.get(`${API}/assets/trending`, { params: { window: "30d", count: 100 }, timeout: 30000 }).then((r) => r.data?.items || []).catch(() => null),
        http.get(`${API}/assets/activity`, { params: { window: "30d", count: 100 }, timeout: 30000 }).then((r) => r.data?.items || []).catch(() => null),
      ]);
      if (t === null && a === null) return;
      const map = new Map();
      [...(t || []), ...(a || [])].forEach((it) => { if (it?.asset && !map.has(it.asset)) map.set(it.asset, it); });
      setIndex([...map.values()]);
    } finally {
      loadingRef.current = false;
      setLoadingIdx(false);
    }
  };

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setFilterOpen(false); } };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setFilterOpen(false); } };
    const onFocusReq = () => {
      boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      loadIndex();
      setTimeout(() => inputRef.current?.focus(), 250);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("ga-focus-asset-search", onFocusReq);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); window.removeEventListener("ga-focus-asset-search", onFocusReq); };
  }, []);

  const ql = q.trim().toLowerCase();
  const { meta, version } = useTokenMeta();
  const results = useMemo(() => {
    if (!ql || !index) return [];
    return index
      .filter((it) => {
        const name = decodeAssetNameSmart(it.asset_name || "").toLowerCase();
        const reg = meta.get(it.asset);
        const regName = `${reg?.name || ""} ${reg?.ticker || ""}`.toLowerCase();
        const fp = (it.fingerprint || "").toLowerCase();
        const pol = (it.policy_id || "").toLowerCase();
        const unit = (it.asset || "").toLowerCase();
        if (filter === "name") return name.includes(ql) || regName.includes(ql);
        if (filter === "fingerprint") return fp.includes(ql);
        if (filter === "policy") return pol.startsWith(ql);
        return name.includes(ql) || regName.includes(ql) || fp.includes(ql) || unit.startsWith(ql) || pol.startsWith(ql);
      })
      .slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ql, index, filter, version]);

  const isHex = HEX.test(ql);
  const directUnit = isHex && ql.length >= 58 && ql.length % 2 === 0 ? ql : null;
  const directPolicy = isHex && ql.length === 56 ? ql : null;

  const pick = (asset) => { onPickAsset(asset); setOpen(false); setQ(""); };
  const openPolicy = (pid) => { onOpenPolicy?.(pid); setOpen(false); setQ(""); };
  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  useRequestMeta(results.map((it) => it.asset));
  // Pre-fetch registry metadata for the WHOLE index so any registered token is
  // findable by its official name/ticker (and its logo is ready instantly).
  useRequestMeta((index || []).map((it) => it.asset));
  const placeholder = filter === "policy" ? "Search by policy ID…" : filter === "fingerprint" ? "Search by fingerprint (asset1…)" : filter === "name" ? "Search by asset name…" : "Search by asset name, policy ID or fingerprint…";

  return (
    <div ref={boxRef} className="relative z-50" data-testid="asset-search-bar">
      <div className="bg-card/50 backdrop-blur-md border border-primary/20 rounded-2xl p-2 flex items-center gap-2">
        {/* filter dropdown */}
        <div className="relative shrink-0">
          <button
            type="button"
            data-testid="asset-filter-button"
            onClick={() => setFilterOpen((v) => !v)}
            className={cn(
              "h-11 px-3.5 inline-flex items-center gap-2 mono uppercase tracking-[0.2em] text-[11px] border rounded-lg transition-colors",
              filterOpen ? "bg-primary/15 border-primary/50 text-primary glow-active" : "bg-black/40 border-primary/20 text-muted-foreground hover:text-primary hover:border-primary/40",
            )}
            aria-haspopup="listbox"
            aria-expanded={filterOpen}
          >
            <Funnel size={13} />
            <span className="hidden sm:inline">{activeFilter.label}</span>
            <CaretDown size={11} className={cn("transition-transform", filterOpen && "rotate-180")} />
          </button>
          {filterOpen ? (
            <div data-testid="asset-filter-dropdown" role="listbox" className="absolute left-0 top-full mt-2 z-[60] min-w-[200px] bg-card/95 backdrop-blur-md border border-primary/30 rounded-lg shadow-xl shadow-primary/10 py-1.5">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="option"
                  aria-selected={f.key === filter}
                  data-testid={`asset-filter-${f.key}`}
                  onClick={() => { setFilter(f.key); setFilterOpen(false); loadIndex(); inputRef.current?.focus(); }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-[11px] mono uppercase tracking-[0.2em] flex items-center gap-2 transition-colors",
                    f.key === filter ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted/30 hover:text-foreground",
                  )}
                >
                  {f.key === filter ? <Check size={11} /> : <span className="w-3" />}
                  {f.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <span className="h-7 w-px bg-primary/15 shrink-0" aria-hidden="true" />
        <MagnifyingGlass size={18} className="text-primary/70 shrink-0" />
        <input
          ref={inputRef}
          data-testid="asset-search-input"
          value={q}
          onFocus={() => { loadIndex(); setOpen(true); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") { if (directUnit) pick(directUnit); else if (results[0]) pick(results[0].asset); } }}
          placeholder={placeholder}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="none"
          className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[14px] mono text-foreground placeholder:text-muted-foreground/50"
        />
        {loadingIdx ? <CircleNotch size={14} className="animate-spin text-primary shrink-0" /> : null}
        {q ? (
          <button type="button" data-testid="asset-search-clear" onClick={() => { setQ(""); inputRef.current?.focus(); }} className="text-muted-foreground hover:text-foreground shrink-0"><X size={14} /></button>
        ) : (
          <span className="hidden lg:inline text-[11px] mono text-muted-foreground/40 shrink-0 pr-1">e.g. asset1qy… | policy1… | asset1…</span>
        )}
        <span className="h-7 w-px bg-primary/15 shrink-0 hidden sm:block" aria-hidden="true" />
        <div className="shrink-0 hidden sm:block">
          <WindowTabs value={win} onChange={setWin} testid="dashboard-window" />
        </div>
      </div>

      {/* mobile window toggle (the inline one is hidden < sm) */}
      <div className="sm:hidden mt-2 flex items-center justify-between gap-2 px-1">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">window</span>
        <WindowTabs value={win} onChange={setWin} testid="dashboard-window-mobile" />
      </div>

      {/* results dropdown */}
      {open && (ql || loadingIdx) ? (
        <div data-testid="asset-search-results" className="absolute z-[55] mt-2 left-0 right-0 bg-card/95 backdrop-blur border border-primary/25 rounded-xl overflow-hidden shadow-2xl shadow-primary/10 max-h-96 overflow-y-auto">
          {directUnit ? (
            <button type="button" data-testid="asset-search-direct" onClick={() => pick(directUnit)} className="w-full text-left px-4 py-3 text-[12px] mono text-[#22d3ee] hover:bg-primary/10 border-b border-primary/10 inline-flex items-center gap-2">
              <ArrowSquareOut size={13} /> Open asset id {truncMid(directUnit, 12, 8)}
            </button>
          ) : null}
          {directPolicy && onOpenPolicy ? (
            <button type="button" data-testid="asset-search-policy-direct" onClick={() => openPolicy(directPolicy)} className="w-full text-left px-4 py-3 text-[12px] mono text-primary hover:bg-primary/10 border-b border-primary/10 inline-flex items-center gap-2">
              <Stack size={13} /> View all assets under policy {truncMid(directPolicy, 10, 6)}
            </button>
          ) : null}
          {loadingIdx && !index ? <div className="px-4 py-3 text-[12px] text-muted-foreground mono">building search index…</div> : null}
          {!loadingIdx && index
            ? results.map((it, i) => {
                const reg = meta.get(it.asset);
                const { text } = assetLabel(it);
                const display = reg?.name || reg?.ticker || text;
                return (
                  <button key={it.asset} type="button" data-testid={`asset-search-result-${i}`} onClick={() => pick(it.asset)} className="w-full text-left px-4 py-2.5 hover:bg-primary/10 flex items-center gap-3 border-b border-primary/5 last:border-b-0">
                    <TokenLogo item={it} size={24} />
                    <span className="flex items-center gap-1 min-w-0 flex-1">
                      <span className="truncate text-[13px] mono text-foreground">{display}</span>
                      {reg?.name ? <VerifiedTick /> : null}
                      <Cip68Badge assetName={it.asset_name} />
                    </span>
                    <span className="text-[10px] mono text-muted-foreground shrink-0">{it.fingerprint ? `${it.fingerprint.slice(0, 12)}…` : ""}</span>
                  </button>
                );
              })
            : null}
          {!loadingIdx && index && ql && !results.length && !directUnit && !directPolicy ? (
            <div data-testid="asset-search-empty" className="px-4 py-3 text-[12px] text-muted-foreground mono">No match in trending/active assets. Paste a full asset or policy id to open it directly.</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
